<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

import Draggable from 'vuedraggable';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import KanbanColumn from './KanbanColumn.vue';

const props = defineProps({
  boardId: {
    type: Number,
    required: true,
  },
  board: {
    type: Object,
    required: true,
  },
  columns: {
    type: Array,
    default: () => [],
  },
  cards: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const columnDialogRef = ref(null);
const editingColumn = ref(null);
const columnName = ref('');
const isSavingColumn = ref(false);
const columnToDelete = ref(null);
const showColumnDeletePopup = ref(false);
const isDeletingColumn = ref(false);

const cardsForColumn = columnId => {
  return props.cards.filter(card => card.kanban_column_id === columnId);
};

const localColumns = ref(props.columns);

watch(
  () => props.columns,
  newColumns => {
    localColumns.value = newColumns;
  },
  { deep: true }
);

let columnDragStart = [];

const onColumnDragStart = () => {
  columnDragStart = localColumns.value.map(column => column.id);
};

const onColumnDragEnd = async () => {
  const orderedIds = localColumns.value.map(column => column.id);
  const isNoOp = orderedIds.every((id, index) => id === columnDragStart[index]);
  if (isNoOp) {
    return;
  }
  try {
    await store.dispatch('kanban/reorderColumns', {
      boardId: props.boardId,
      orderedIds,
    });
  } catch (error) {
    useAlert(t('KANBAN.COLUMNS.API.REORDER_ERROR'));
  }
};

const openCreateColumnDialog = () => {
  editingColumn.value = null;
  columnName.value = '';
  columnDialogRef.value?.open();
};

const openEditColumnDialog = column => {
  editingColumn.value = column;
  columnName.value = column.name;
  columnDialogRef.value?.open();
};

const saveColumn = async () => {
  const name = columnName.value.trim();
  if (!name) {
    return;
  }
  isSavingColumn.value = true;
  try {
    if (editingColumn.value) {
      await store.dispatch('kanban/updateColumn', {
        boardId: props.boardId,
        columnId: editingColumn.value.id,
        name,
      });
      useAlert(t('KANBAN.COLUMNS.API.SUCCESS_UPDATE'));
    } else {
      await store.dispatch('kanban/createColumn', {
        boardId: props.boardId,
        name,
        position: props.columns.length,
      });
      useAlert(t('KANBAN.COLUMNS.API.SUCCESS_CREATE'));
    }
    columnDialogRef.value?.close();
  } catch (error) {
    useAlert(t('KANBAN.COLUMNS.API.ERROR_MESSAGE'));
  } finally {
    isSavingColumn.value = false;
  }
};

const openColumnDeletePopup = column => {
  columnToDelete.value = column;
  showColumnDeletePopup.value = true;
};

const closeColumnDeletePopup = () => {
  showColumnDeletePopup.value = false;
};

const confirmColumnDeletion = async () => {
  isDeletingColumn.value = true;
  try {
    await store.dispatch('kanban/deleteColumn', {
      boardId: props.boardId,
      columnId: columnToDelete.value.id,
    });
    useAlert(t('KANBAN.COLUMNS.API.SUCCESS_DELETE'));
    closeColumnDeletePopup();
  } catch (error) {
    useAlert(t('KANBAN.COLUMNS.API.ERROR_MESSAGE'));
  } finally {
    isDeletingColumn.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col flex-1 h-full min-h-0">
    <div class="flex items-start justify-between gap-4 px-1 pt-4 pb-3">
      <div class="flex flex-col gap-1 min-w-0">
        <h2 class="text-lg font-medium text-n-slate-12 truncate">
          {{ board.name }}
        </h2>
        <p
          v-if="board.description"
          class="text-sm text-n-slate-10 line-clamp-2"
        >
          {{ board.description }}
        </p>
      </div>
      <Button
        v-if="isAdmin"
        size="sm"
        class="flex-shrink-0"
        :label="t('KANBAN.COLUMNS.NEW_COLUMN')"
        icon="i-lucide-plus"
        @click="openCreateColumnDialog"
      />
    </div>

    <div
      v-if="isLoading && !columns.length"
      class="flex items-center justify-center flex-1"
    >
      <Spinner class="text-n-slate-10" />
    </div>

    <div
      v-else-if="!columns.length"
      class="flex flex-col items-center justify-center flex-1 gap-3"
    >
      <span
        class="flex items-center justify-center w-12 h-12 rounded-full bg-n-alpha-2"
      >
        <Icon icon="i-lucide-columns-3" class="size-6 text-n-slate-10" />
      </span>
      <div class="flex flex-col items-center gap-1">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.COLUMNS.EMPTY_TITLE') }}
        </h3>
        <p class="max-w-sm text-sm text-center text-n-slate-10">
          {{ t('KANBAN.COLUMNS.EMPTY_DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="isAdmin"
        :label="t('KANBAN.COLUMNS.NEW_COLUMN')"
        icon="i-lucide-plus"
        @click="openCreateColumnDialog"
      />
    </div>

    <div v-else class="flex flex-1 gap-4 min-h-0 py-1 pb-4 overflow-x-auto">
      <Draggable
        v-model="localColumns"
        item-key="id"
        tag="div"
        :disabled="!isAdmin"
        class="flex flex-shrink-0 gap-4"
        ghost-class="opacity-40"
        @start="onColumnDragStart"
        @end="onColumnDragEnd"
      >
        <template #item="{ element }">
          <div class="flex-shrink-0 h-full">
            <KanbanColumn
              :board-id="boardId"
              :column="element"
              :cards="cardsForColumn(element.id)"
              @edit-column="openEditColumnDialog"
              @delete-column="openColumnDeletePopup"
            />
          </div>
        </template>
      </Draggable>
      <button
        v-if="isAdmin"
        type="button"
        class="flex items-center justify-center w-72 h-16 flex-shrink-0 gap-2 text-sm text-n-slate-10 border border-dashed rounded-xl border-n-strong dark:border-n-strong/50 hover:border-n-brand/60 hover:text-n-brand transition-colors"
        @click="openCreateColumnDialog"
      >
        <Icon icon="i-lucide-plus" class="size-4" />
        {{ t('KANBAN.COLUMNS.NEW_COLUMN') }}
      </button>
    </div>

    <Dialog
      ref="columnDialogRef"
      :title="
        editingColumn
          ? t('KANBAN.COLUMNS.EDIT_TITLE')
          : t('KANBAN.COLUMNS.CREATE_TITLE')
      "
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="columnName"
          :label="t('KANBAN.COLUMNS.NAME_LABEL')"
          :placeholder="t('KANBAN.COLUMNS.NAME_PLACEHOLDER')"
          autofocus
        />
      </div>
      <template #footer>
        <div class="flex items-center justify-between w-full gap-3">
          <Button
            variant="faded"
            class="w-full"
            :label="t('KANBAN.COLUMNS.CANCEL')"
            @click="columnDialogRef?.close()"
          />
          <Button
            color="blue"
            class="w-full"
            :label="t('KANBAN.COLUMNS.SAVE')"
            :is-loading="isSavingColumn"
            :disabled="isSavingColumn || !columnName.trim()"
            type="submit"
            @click="saveColumn"
          />
        </div>
      </template>
    </Dialog>

    <woot-delete-modal
      v-model:show="showColumnDeletePopup"
      :on-close="closeColumnDeletePopup"
      :on-confirm="confirmColumnDeletion"
      :title="t('KANBAN.COLUMNS.DELETE_TITLE')"
      :message="t('KANBAN.COLUMNS.DELETE_CONFIRM_MESSAGE')"
      :confirm-text="t('KANBAN.COLUMNS.CONFIRM_DELETE')"
      :reject-text="t('KANBAN.COLUMNS.REJECT_DELETE')"
    />
  </div>
</template>
